import { Component, inject } from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { MenuModule } from 'primeng/menu';
import { CardModule } from 'primeng/card';
import { AuthService } from '../../services/auth.service';
import { ThemeService } from '../../services/theme.service';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, MenuModule, CardModule],
  templateUrl: './navbar.html',
  styleUrl: './navbar.scss',
})
export class NavbarComponent {
  auth = inject(AuthService);
  router = inject(Router);
  themeService = inject(ThemeService);
  profileMenuWidth = 180;

  get isDarkMode(): boolean {
    return this.themeService.isDarkMode();
  }

  get userAvatarColor(): string {
    const user = this.auth.getUser();
    return String(user?.avatar_color || user?.avatarColor || '').trim() || 'var(--brand-primary)';
  }

  get userRole(): string {
    return this.auth.normalizeRoleForCompare(this.auth.getUser()?.role);
  }

  get userInitials(): string {
    const user = this.auth.getUser();
    const directInitials = String(user?.initials || '')
      .trim()
      .toUpperCase();
    if (directInitials) {
      return directInitials.slice(0, 2);
    }

    const parts = String(user?.name || '')
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    if (!parts.length) return '';
    return (
      parts.length === 1 ? parts[0].slice(0, 2) : `${parts[0][0]}${parts[1][0]}`
    ).toUpperCase();
  }

  get links() {
    const role = this.auth.normalizeRoleForCompare(this.auth.getUser()?.role);

    const claimsRoles = [
      'CLAIMS REPORTING',
      'CLAIMS SUPERVISOR',
      'CLAIMS ADJUSTER',
      'CLAIMS COMMITTEE',
      'CLAIMS MANAGER',
    ];

    const baseLinks = [
      {
        path: '/dashboard',
        label: 'Dashboard',
        role: [...claimsRoles, 'ADMIN (ALL)', 'SUPERADMIN', 'LEGAL USER'],
      },
      {
        path: '/claim-intake',
        label: 'Matter Intake',
        role: [...claimsRoles, 'ADMIN (ALL)', 'SUPERADMIN', 'LEGAL USER'],
      },
      // { path: '/matter-intake', label: 'Matter Intake (SUPERADMIN)', role: ['SUPERADMIN'] },
      {
        path: '/edge-matters',
        label: 'Matter Management',
        role: [...claimsRoles, 'ADMIN (ALL)', 'SUPERADMIN', 'LEGAL USER'],
      },
      {
        path: '/submissions',
        label: 'Submissions',
        role: [...claimsRoles, 'ADMIN (ALL)', 'SUPERADMIN', 'LEGAL USER'],
      },
      {
        path: '/activity',
        label: 'Activity',
        role: [...claimsRoles, 'ADMIN (ALL)', 'SUPERADMIN', 'LEGAL USER'],
      },
      {
        path: '/matter-notes',
        label: 'Matter Notes',
        role: ['ADMIN (ALL)', 'SUPERADMIN', 'LEGAL USER'],
      },
      {
        path: '/edge-files',
        label: 'Matter Documents',
        role: ['ADMIN (ALL)', 'SUPERADMIN', 'LEGAL USER'],
      },
      // {
      //   path: '/calendar',
      //   label: 'Calendar',
      //   role: [...claimsRoles, 'ADMIN (ALL)', 'SUPERADMIN', 'LEGAL USER'],
      // },

      { path: '/admin', label: 'Admin', role: ['ADMIN (ALL)', 'SUPERADMIN'] },
    ];

    // Only show Admin link for admin users, SUPERADMIN link for SUPERADMIN users

    return baseLinks.filter(
      (link) =>
        !link.role ||
        link.role.map((value) => this.auth.normalizeRoleForCompare(value)).includes(role),
    );
  }

  profileItems = [
    {
      label: 'Logout',
      icon: 'pi pi-sign-out',
      command: () => {
        this.logout();
      },
    },
  ];

  openProfileMenu(event: Event, menu: { toggle: (ev: Event) => void }) {
    const trigger = event.currentTarget as HTMLElement | null;
    if (trigger) {
      this.profileMenuWidth = Math.ceil(trigger.getBoundingClientRect().width);
    }
    menu.toggle(event);
  }

  toggleTheme(): void {
    this.themeService.toggleTheme();
  }

  logout() {
    this.auth.clearUserData();
    sessionStorage.clear();
    localStorage.clear();
    this.router.navigate(['/login']);
  }

}
